import { useEffect, useRef, useState } from 'react'
import type { Transcript, LineId } from '../../contract'
import type { Client } from '../api'
import { fmtTime, speakerHue, fmtDate } from '../format'
import { navigate, type Route } from '../router'

interface Props {
  client: Client
  transcriptId: string
  lineId?: LineId
  route: Route
  onClose: () => void
}

/** Transcript reader: timestamps + speakers in the gutter, every line addressable by `#t=<lineId>`. */
export function Reader({ client, transcriptId, lineId, route, onClose }: Props) {
  const [transcript, setTranscript] = useState<Transcript | null>(null)
  const [error, setError] = useState<string | null>(null)
  const listRef = useRef<HTMLOListElement>(null)

  useEffect(() => {
    let live = true
    setTranscript(null); setError(null)
    client.getTranscript(transcriptId).then((t) => live && setTranscript(t), (e) => live && setError(String(e.message ?? e)))
    return () => { live = false }
  }, [client, transcriptId])

  useEffect(() => {
    if (!transcript || !lineId) return
    const el = listRef.current?.querySelector<HTMLElement>(`[data-line-id="${CSS.escape(lineId)}"]`)
    if (!el) return
    el.scrollIntoView({ block: 'center', behavior: 'auto' })
    el.focus({ preventScroll: true })
  }, [transcript, lineId])

  const missing = transcript && lineId && !transcript.lines.some((l) => l.id === lineId)

  return (
    <section className="reader" aria-label="Transcript reader" data-testid="reader">
      <header className="reader__head">
        <div className="reader__titles">
          <div className="reader__kicker">
            {transcript?.sample && <span className="badge badge--sample">Sample</span>}
            <span className="mono">{transcriptId}</span>
          </div>
          <h2 className="reader__title">{transcript?.title ?? (error ? 'Could not load' : 'Loading…')}</h2>
          {transcript && <div className="reader__meta">{transcript.lines.length} lines · {fmtDate(transcript.createdAt)}</div>}
        </div>
        <button className="iconbtn" onClick={onClose} aria-label="Close reader" title="Close reader">✕</button>
      </header>
      {error && <p className="error">{error}</p>}
      {missing && <p className="notice">Line <span className="mono">{lineId}</span> is not in this transcript.</p>}
      {transcript && (
        <ol className="lines" ref={listRef}>
          {transcript.lines.map((l) => {
            const active = l.id === lineId
            return (
              <li
                key={l.id}
                id={`t=${l.id}`}
                data-line-id={l.id}
                data-active={active || undefined}
                className={`line${active ? ' line--active' : ''}`}
                tabIndex={-1}
                onClick={() => navigate({ ...route, t: l.id, r: transcriptId })}
                title={l.id}
              >
                <span className="line__time mono" data-testid="line-time">{l.t === null ? '' : fmtTime(l.t)}</span>
                {l.speaker
                  ? <span className="line__speaker" style={{ ['--hue' as string]: speakerHue(l.speaker) }}>{l.speaker}</span>
                  : <span className="line__speaker line__speaker--none" aria-hidden />}
                <span className="line__text">{l.text}</span>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
